// src/components/module/almacen/ArticulosModal/ArticulosModal.tsx
// Rediseño corporativo estilo Notion/Linear
import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogContent,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  MenuItem,
  Grid,
  Box,
  Typography,
  Stack,
  IconButton,
  alpha,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import { Close as CloseIcon, Save, Inventory, ArrowBack } from "@mui/icons-material";
import { ArticuloDto } from "../../../../services/api/articulosApi";
import { useMaintenanceApi } from "../../../../services/api/maintenanceApi";
import { useImpuestosApi } from "../../../../services/api/impuestosApi";
import { DarkModeContext } from "../../../../context/darkModeContext";
const P = {
  light: { bg: "#FFFFFF", bgS: "#F7F7F5", bgT: "#F1F1EF", text: "#37352F", textS: "#6B6B6B", textT: "#9B9A97", border: "#E9E9E7", borderH: "#D3D3D0", accent: "#2383E2", accentBg: "#E8F0FE", success: "#0F7B6C", hover: "rgba(55,53,47,0.04)" },
  dark: { bg: "#191919", bgS: "#202020", bgT: "#2B2B2B", text: "#E8E8E6", textS: "#9B9A97", textT: "#6B6B6B", border: "#333333", borderH: "#444444", accent: "#529CCA", accentBg: "#143A52", success: "#4DAB9A", hover: "rgba(255,255,255,0.04)" },
};
const FieldLabel: React.FC<{ label: string; required?: boolean; color: string }> = ({ label, required, color }) => (
  <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color, mb: 0.5, textTransform: "uppercase", letterSpacing: "0.04em" }}>
    {label}{required && <Box component="span" sx={{ color: "#E03E3E", ml: 0.3 }}>*</Box>}
  </Typography>
);
interface ArticulosModalProps {
  isOpen: boolean; onClose: () => void; onSubmit: (data: ArticuloDto) => Promise<void>;
  title: string; initialData?: ArticuloDto; isLoading: boolean; isEdit?: boolean;
}
const ArticulosModal: React.FC<ArticulosModalProps> = ({ isOpen, onClose, onSubmit, title, initialData, isLoading, isEdit = false }) => {
  const { darkMode } = useContext(DarkModeContext);
  const p = darkMode ? P.dark : P.light;
  const [formData, setFormData] = useState<ArticuloDto>({ IdCategoria: 0, IdEmbalaje: 0, IdImpuesto: 0, Nombre: "", Descripcion: "", FechaAlta: new Date().toISOString().split("T")[0], EAN: "", UnidadesPorEmbalaje: 1, Referencia: "", PrecioCompra: 0, PrecioVenta: 0, Descuento: 0, Activo: true });
  const { getAll: getCategorias } = useMaintenanceApi("categorias");
  const { getAll: getEmbalajes } = useMaintenanceApi("tipos-embalaje");
  const { getAll: getImpuestos } = useImpuestosApi();
  const [categorias, setCategorias] = useState<any[]>([]);
  const [embalajes, setEmbalajes] = useState<any[]>([]);
  const [impuestos, setImpuestos] = useState<any[]>([]);
  useEffect(() => {
    const load = async () => {
      const [c, e, i] = await Promise.all([getCategorias(), getEmbalajes(), getImpuestos()]);
      setCategorias(c.filter((x: any) => x.Activo)); setEmbalajes(e.filter((x: any) => x.Activo)); setImpuestos(i.filter((x: any) => x.Activo));
    };
    load();
  }, []);
  useEffect(() => {
    setFormData(initialData || { IdCategoria: 0, IdEmbalaje: 0, IdImpuesto: 0, Nombre: "", Descripcion: "", FechaAlta: new Date().toISOString().split("T")[0], EAN: "", UnidadesPorEmbalaje: 1, Referencia: "", PrecioCompra: 0, PrecioVenta: 0, Descuento: 0, Activo: true });
  }, [initialData, isOpen]);
  const handleSubmit = async (e: React.FormEvent) => { e.preventDefault(); await onSubmit({ ...formData, PrecioCompra: Number(Number(formData.PrecioCompra).toFixed(2)), PrecioVenta: Number(Number(formData.PrecioVenta).toFixed(2)) }); };
  const set = (field: string, value: any) => setFormData({ ...formData, [field]: value });
  const inputSx = { "& .MuiInputBase-root": { bgcolor: p.bgS } };
  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: "14px", border: `1px solid ${p.border}`, bgcolor: p.bg, overflow: "hidden" } }}>
        {/* Header */}
        <Box sx={{ px: 3, py: 2.5, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${p.border}` }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ width: 40, height: 40, borderRadius: "10px", bgcolor: p.accentBg, display: "flex", alignItems: "center", justifyContent: "center", color: p.accent }}>
              <Inventory sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: "1.05rem", fontWeight: 700, color: p.text }}>{title}</Typography>
              <Typography sx={{ fontSize: "0.75rem", color: p.textT }}>Complete la información del artículo</Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} sx={{ width: 32, height: 32, borderRadius: "8px", color: p.textT, "&:hover": { bgcolor: p.bgT } }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ p: 3, bgcolor: p.bgS }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={6}>
                <FieldLabel label="Categoría" required color={p.textT} />
                <TextField select size="small" fullWidth value={formData.IdCategoria} onChange={(e) => set("IdCategoria", Number(e.target.value))} sx={inputSx}>
                  <MenuItem value={0} disabled>Seleccione</MenuItem>
                  {categorias.map(c => <MenuItem key={c.Id} value={c.Id}>{c.Nombre}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <FieldLabel label="Código EAN" required color={p.textT} />
                <TextField size="small" fullWidth value={formData.EAN} onChange={(e) => set("EAN", e.target.value)} sx={{ ...inputSx, "& .MuiInputBase-input": { fontFamily: "'IBM Plex Mono', monospace" } }} />
              </Grid>
              <Grid item xs={12}>
                <FieldLabel label="Nombre del Artículo" required color={p.textT} />
                <TextField size="small" fullWidth value={formData.Nombre} onChange={(e) => set("Nombre", e.target.value)} sx={inputSx} />
              </Grid>
              <Grid item xs={12}>
                <FieldLabel label="Descripción" color={p.textT} />
                <TextField size="small" fullWidth multiline rows={2} value={formData.Descripcion} onChange={(e) => set("Descripcion", e.target.value)} sx={inputSx} />
              </Grid>
              <Grid item xs={12} md={6}>
                <FieldLabel label="Tipo de Embalaje" required color={p.textT} />
                <TextField select size="small" fullWidth value={formData.IdEmbalaje} onChange={(e) => set("IdEmbalaje", Number(e.target.value))} sx={inputSx}>
                  <MenuItem value={0} disabled>Seleccione</MenuItem>
                  {embalajes.map(e => <MenuItem key={e.Id} value={e.Id}>{e.Nombre}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <FieldLabel label="Unidades por Embalaje" required color={p.textT} />
                <TextField type="number" size="small" fullWidth value={formData.UnidadesPorEmbalaje} onChange={(e) => set("UnidadesPorEmbalaje", Number(e.target.value))} onFocus={(e) => e.target.select()} inputProps={{ min: 1 }} sx={inputSx} />
              </Grid>
              <Grid item xs={12} md={6}>
                <FieldLabel label="Impuesto" required color={p.textT} />
                <TextField select size="small" fullWidth value={formData.IdImpuesto} onChange={(e) => set("IdImpuesto", Number(e.target.value))} sx={inputSx}>
                  <MenuItem value={0} disabled>Seleccione</MenuItem>
                  {impuestos.map(i => <MenuItem key={i.Id} value={i.Id}>{i.Nombre}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <FieldLabel label="Referencia" color={p.textT} />
                <TextField size="small" fullWidth value={formData.Referencia} onChange={(e) => set("Referencia", e.target.value)} sx={inputSx} />
              </Grid>
              <Grid item xs={12} md={4}>
                <FieldLabel label="Precio de Compra" required color={p.textT} />
                <TextField type="number" size="small" fullWidth value={formData.PrecioCompra} onChange={(e) => set("PrecioCompra", Number(e.target.value))} onFocus={(e) => e.target.select()}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Typography sx={{ fontSize: "0.8rem", color: p.textT }}>$</Typography></InputAdornment> }}
                  inputProps={{ min: 0, step: 0.01 }} sx={{ ...inputSx, "& .MuiInputBase-input": { fontFamily: "'IBM Plex Mono', monospace" } }} />
              </Grid>
              <Grid item xs={12} md={4}>
                <FieldLabel label="Precio de Venta" required color={p.textT} />
                <TextField type="number" size="small" fullWidth value={formData.PrecioVenta} onChange={(e) => set("PrecioVenta", Number(e.target.value))} onFocus={(e) => e.target.select()}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Typography sx={{ fontSize: "0.8rem", color: p.textT }}>$</Typography></InputAdornment> }}
                  inputProps={{ min: 0, step: 0.01 }} sx={{ ...inputSx, "& .MuiInputBase-input": { fontFamily: "'IBM Plex Mono', monospace" } }} />
              </Grid>
              <Grid item xs={12} md={4}>
                <FieldLabel label="Descuento (%)" required color={p.textT} />
                <TextField type="number" size="small" fullWidth value={formData.Descuento} onChange={(e) => set("Descuento", Number(e.target.value))} onFocus={(e) => e.target.select()}
                  InputProps={{ endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: "0.8rem", color: p.textT }}>%</Typography></InputAdornment> }}
                  inputProps={{ min: 0, max: 100, step: 1 }} sx={inputSx} />
              </Grid>
              {!isEdit && (
                <Grid item xs={12}>
                  <FormControlLabel control={<Switch checked={formData.Activo} onChange={(e) => set("Activo", e.target.checked)} sx={{ "& .Mui-checked": { color: p.accent }, "& .Mui-checked + .MuiSwitch-track": { bgcolor: p.accent } }} size="small" />}
                    label={<Typography sx={{ fontSize: "0.84rem", color: p.textS }}>Activo</Typography>} />
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <Box sx={{ px: 3, py: 2, borderTop: `1px solid ${p.border}`, bgcolor: p.bg, display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
            <Button onClick={onClose} disabled={isLoading} sx={{ color: p.textS, fontSize: "0.82rem", fontWeight: 600, borderRadius: "8px", "&:hover": { bgcolor: p.hover } }}>Cancelar</Button>
            <Button type="submit" variant="contained" disableElevation disabled={isLoading || !formData.IdCategoria || !formData.IdEmbalaje || !formData.IdImpuesto || !formData.Nombre || !formData.EAN}
              startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <Save sx={{ fontSize: 16 }} />}
              sx={{ bgcolor: p.accent, color: "#FFF", px: 3, fontSize: "0.82rem", fontWeight: 600, borderRadius: "8px", "&:hover": { bgcolor: darkMode ? "#6BAFDB" : "#1B6FC2" } }}>
              {isLoading ? "Guardando..." : isEdit ? "Actualizar" : "Guardar"}
            </Button>
          </Box>
        </form>
      </Dialog>
  );
};
export default ArticulosModal;
